import { Form, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines } from '@fortawesome/free-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

const CONTEXT_WINDOW_TOKENS = 1000000;
export const SELECTED_FILES_CONTEXT_USAGE_THRESHOLD = 700000;

function FileSelector({
  show,
  onHide,
  claimFiles,
  selectedFileIds,
  onSelectedFilesChange,
  user,
}) {
  // Calculate total tokens for selected files
  const totalSelectedTokens = claimFiles
    .filter((f) => selectedFileIds.includes(f.fileId))
    .reduce((sum, f) => sum + (f.fileMeta?.fileContentTokensO200kBase || 0), 0);

  const contextUsagePercent = Math.round(
    (totalSelectedTokens / CONTEXT_WINDOW_TOKENS) * 100,
  );
  const isWarningLevel =
    totalSelectedTokens > SELECTED_FILES_CONTEXT_USAGE_THRESHOLD;

  // Show token count only in dev environment or for @inovo.io users
  const shouldShowTokenCount =
    process.env.NODE_ENV === 'development' ||
    user?.email?.endsWith('@inovo.io');

  const handleSelectAll = () => {
    // All files can now be selected (large file support via RAG)
    onSelectedFilesChange(claimFiles.map((f) => f.fileId));
  };

  const handleDeselectAll = () => {
    onSelectedFilesChange([]);
  };

  const toggleFileSelection = (fileId) => {
    // All files can now be selected (large file support via RAG)
    if (selectedFileIds.includes(fileId)) {
      onSelectedFilesChange(selectedFileIds.filter((id) => id !== fileId));
    } else {
      onSelectedFilesChange([...selectedFileIds, fileId]);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header style={{ borderBottom: 'none', paddingBottom: '0' }}>
        <div style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <Modal.Title style={{ fontSize: '20px', fontWeight: '600' }}>
              Context Files
            </Modal.Title>
            <span
              style={{
                fontSize: '14px',
                color: '#6b7280',
                fontWeight: '500',
              }}
            >
              {selectedFileIds.length} of {claimFiles.length}
            </span>
          </div>
          <p
            style={{
              margin: '0',
              fontSize: '14px',
              color: '#6b7280',
            }}
          >
            Select files to include in your query context
          </p>
        </div>
      </Modal.Header>
      <Modal.Body style={{ paddingTop: '16px' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleSelectAll}
            disabled={claimFiles.length === 0}
            style={{
              flex: 1,
              borderColor: '#d1d5db',
            }}
          >
            Select All
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleDeselectAll}
            disabled={claimFiles.length === 0}
            style={{
              flex: 1,
              borderColor: '#d1d5db',
            }}
          >
            Deselect All
          </Button>
        </div>

        <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
          {claimFiles.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center' }}>
              No files available
            </p>
          ) : (
            claimFiles.map((file) => {
              return (
                <div
                  key={file.fileId}
                  onClick={() => toggleFileSelection(file.fileId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 4px',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <Form.Check
                    type="checkbox"
                    checked={selectedFileIds.includes(file.fileId)}
                    onChange={() => toggleFileSelection(file.fileId)}
                    style={{ margin: '0', marginTop: '2px', flexShrink: 0 }}
                  />
                  <FontAwesomeIcon
                    icon={faFileLines}
                    style={{
                      color: '#0066cc',
                      flexShrink: 0,
                      fontSize: '15px',
                      marginTop: '2px',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#111827',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {file.fileName}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Modal.Body>
      {shouldShowTokenCount && (
        <Modal.Footer
          style={{
            borderTop: isWarningLevel
              ? '2px solid #fca5a5'
              : '1px solid #e5e7eb',
            paddingTop: '12px',
            backgroundColor: isWarningLevel ? '#fef2f2' : 'transparent',
          }}
        >
          <div
            id="file-selector-context-usage"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {isWarningLevel && (
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  style={{
                    color: '#dc2626',
                    fontSize: '16px',
                    flexShrink: 0,
                  }}
                />
              )}
              <span
                style={{
                  fontSize: '13px',
                  color: isWarningLevel ? '#dc2626' : '#6b7280',
                  fontWeight: isWarningLevel ? '500' : '400',
                }}
              >
                {selectedFileIds.length === 0
                  ? 'No files selected'
                  : `Selected files use ${contextUsagePercent}% of context window`}
              </span>
            </div>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default FileSelector;
